import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertCircle, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get(`/staff-admin/invitations/by-token/${token}`);
        if (mounted) setInvite(data);
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || "Zaproszenie nieprawidłowe");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [token]);

  async function activate() {
    if (pwd.length < 10) { toast.error("Hasło musi mieć min. 10 znaków"); return; }
    if (pwd !== pwd2) { toast.error("Hasła nie są takie same"); return; }
    setBusy(true);
    try {
      await api.post("/staff-admin/invitations/accept", { token, password: pwd });
      setDone(true);
      toast.success("Konto aktywowane! Zaloguj się.");
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd aktywacji");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tt-surface-2)] p-4">
      <Card className="w-full max-w-md tt-card">
        <CardContent className="p-6 space-y-4">
          <div className="text-center">
            <ShieldCheck className="h-12 w-12 mx-auto text-[var(--tt-primary)] mb-2" />
            <h1 className="text-xl font-semibold tt-display">Aktywacja konta</h1>
            <p className="text-sm text-[var(--tt-muted)]">TO THAI Control Center</p>
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-6 text-[var(--tt-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" />Sprawdzam zaproszenie…
            </div>
          )}

          {error && (
            <div className="p-3 rounded bg-red-50 border border-red-200 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-700 mt-0.5" />
              <div className="text-sm text-red-900">
                <div className="font-semibold">Nie można zaakceptować zaproszenia</div>
                <div>{error}</div>
                <p className="mt-2 text-xs">Skontaktuj się z Ownerem, żeby otrzymać nowe zaproszenie.</p>
              </div>
            </div>
          )}

          {invite && !done && (
            <>
              <div className="p-3 rounded bg-emerald-50 border border-emerald-200">
                <div className="text-sm">
                  <div><b>Witaj, {invite.full_name}!</b></div>
                  <div className="text-emerald-800 text-xs mt-1">
                    Email: <b>{invite.email}</b><br />
                    Rola: <b>{invite.role}</b>{invite.department && <> • Dział: <b>{invite.department}</b></>}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Hasło (min. 10 znaków)</Label>
                  <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                          placeholder="Minimum 10 znaków, np. silnehasło2026"
                          data-testid="accept-pwd" />
                </div>
                <div>
                  <Label className="text-xs">Powtórz hasło</Label>
                  <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                          data-testid="accept-pwd2" />
                </div>
              </div>
              <Button onClick={activate} disabled={busy} className="w-full" data-testid="accept-activate">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                Ustaw hasło i aktywuj
              </Button>
            </>
          )}

          {done && (
            <div className="p-4 rounded bg-emerald-50 border border-emerald-200 text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-700 mb-2" />
              <div className="font-semibold text-emerald-900">Konto aktywowane!</div>
              <div className="text-xs text-emerald-800 mt-1">Przekierowuję do logowania…</div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
